import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnInit
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, concatMap, from, of } from 'rxjs';
import { FileContent, File as FileModel, Note } from 'src/app/classes/models';
import { AuthService } from 'src/app/services/auth.service';
import { NoteService } from 'src/app/services/note.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-note-modal',
  templateUrl: './note-modal.component.html',
  styleUrls: ['./note-modal.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class NoteModalComponent implements OnInit {
  constructor(
    private AuthService: AuthService,
    private NoteService: NoteService,
    private Modal: NgbActiveModal,
    private ToastService: ToastService,
    private UtilityService: UtilityService
  ) {}

  CompanyId?: number;
  VendorId?: number;
  ExemptVendorId?: number;
  ClientRegisteredVendorId?: number;
  UserId?: number;
  ticketId?: number;
  EmailId?: number;

  titleAction: 'Create' | 'Edit' | 'View' = 'Create';
  canEditNote: boolean = false;

  @Input() Note?: Note;
  @Input() NoteId?: number;
  @Input() titleOptions?: string[];
  @Input() disableTitle: boolean = false;
  @Input() allowFileUpload: boolean = false;

  // Used to prevent race condition by determining when the file observable has finished processing
  // a file, then allowing the close action of the modal.
  FileObservable?: Observable<FileModel>;
  Files: FileModel[] = [];

  NoteFormGroup = new FormGroup<{
    id: FormControl<number | undefined>;
    title: FormControl<string>;
    note: FormControl<string>;

    CompanyId?: FormControl<number | null>;
    VendorId?: FormControl<number | null>;
    ExemptVendorId?: FormControl<number | null>;
    ClientRegisteredVendorId?: FormControl<number | null>;
    UserId?: FormControl<number | null>;
    ticketId?: FormControl<string | null>;
    EmailId?: FormControl<number | null>;
    isSensitive?: FormControl<boolean>;
  }>({
    id: new FormControl<number | undefined>(
      { value: undefined, disabled: false },
      { nonNullable: true }
    ),
    title: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required]
    }),
    note: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required]
    })
  });

  addFile({ data: File }: { data: File }) {
    const { name: filename, type: filetype, size: filesize } = File;

    // Convert file into array buffer
    this.FileObservable = from(File.arrayBuffer()).pipe(
      concatMap((filecontents) => {
        const base64EncodedFileContents =
          this.UtilityService.convertArrayBufferToBase64EncodedString(
            filecontents
          );

        return of(
          new FileModel({
            filename,
            filetype,
            filesize,
            FileContent: new FileContent({
              filecontents: base64EncodedFileContents
            })
          })
        );
      })
    );

    this.FileObservable.subscribe({
      next: (File) => {
        this.Files.push(File);
      }
    });
  }

  isAdminUser() {
    const User = this.AuthService.getUser();

    const { UserAccountRole } = User;

    if (!UserAccountRole) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing user role permissions.',
        { returnError: true }
      );
    }

    const { role } = UserAccountRole;

    if (!role) {
      throw this.ToastService.addErrorToast('Error', 'Missing user role.', {
        returnError: true
      });
    }

    return new Set([
      'ADMIN_ADMIN',
      'ADMIN_PRIME_TEAM',
      'ADMIN_EMAILS_TEAM',
      'ADMIN_INSURANCE_TEAM',
      'ADMIN_FRAUD_TEAM',
      'ADMIN_CUSTOMER_SERVICE_TEAM',
      'ADMIN_PROJECT_MANAGEMENT_TEAM'
    ]).has(role);
  }

  saveNote() {
    (() => {
      if (this.FileObservable) {
        return this.FileObservable;
      } else {
        return of<FileModel | null>(null);
      }
    })().subscribe({
      next: () => {
        const errors = this.UtilityService.generateValidationErrorsObject(
          this.NoteFormGroup
        );

        if (errors) {
          throw this.ToastService.addErrorToast(
            'Error',
            'There is an error with one of the fields below.',
            { returnError: true }
          );
        }

        const note: Note = this.NoteFormGroup.value;

        // At least 1 id field is required to be applied to the note.
        const IdFields: (keyof Note)[] = [
          'CompanyId',
          'VendorId',
          'ExemptVendorId',
          'ClientRegisteredVendorId',
          'UserId',
          'ticketId',
          'EmailId'
        ];

        const isIdFieldOnNote = !!IdFields.find((idField) => !!note[idField]);

        if (!isIdFieldOnNote) {
          throw this.ToastService.addErrorToast(
            'Error',
            'There was an error saving the Note.',
            { returnError: true }
          );
        }

        this.Modal.close({ Note: note, Files: this.Files });
      }
    });
  }

  close() {
    this.Modal.dismiss();
  }

  setTitleAndView(currentUserId: number) {
    if (this.Note && this.Note.id) {
      this.titleAction = 'View';

      const { UserId } = this.Note;

      if (currentUserId === UserId) {
        this.titleAction = 'Edit';
        this.canEditNote = true;
      } else {
        this.NoteFormGroup.disable();
        this.NoteFormGroup.updateValueAndValidity();
      }
    } else {
      this.canEditNote = true;
      this.titleAction = 'Create';
    }
  }

  ngOnInit(): void {
    const User = this.AuthService.getUser();

    const { id: currentUserId } = User;

    if (!currentUserId) {
      throw this.ToastService.addErrorToast('Error', 'Missing user id.', {
        returnError: true
      });
    }

    if (this.isAdminUser()) {
      this.NoteFormGroup.setControl(
        'isSensitive',
        new FormControl<boolean>(false, { nonNullable: true })
      );
    }

    if (!this.UserId) {
      this.UserId = currentUserId;
    }

    if (this.Note) {
      this.NoteFormGroup.patchValue(this.Note);

      this.setTitleAndView(currentUserId);
    }

    if (this.NoteId) {
      this.NoteService.getNote(
        this.UtilityService.stringifyObjectsInQueryObject({
          id: this.NoteId
        })
      ).subscribe({
        next: (Note) => {
          this.NoteFormGroup.patchValue(Note);
          this.Note = Note;

          this.setTitleAndView(currentUserId);
        }
      });
    }

    if (!this.Note && !this.NoteId) {
      this.setTitleAndView(currentUserId);
    }

    const fieldIds: {
      field:
        | 'CompanyId'
        | 'VendorId'
        | 'ExemptVendorId'
        | 'ClientRegisteredVendorId'
        | 'UserId'
        | 'ticketId'
        | 'EmailId';
      value: number | undefined;
    }[] = [
      { field: 'CompanyId', value: this.CompanyId },
      { field: 'VendorId', value: this.VendorId },
      { field: 'ExemptVendorId', value: this.ExemptVendorId },
      {
        field: 'ClientRegisteredVendorId',
        value: this.ClientRegisteredVendorId
      },
      { field: 'UserId', value: this.UserId },
      { field: 'ticketId', value: this.ticketId },
      { field: 'EmailId', value: this.EmailId }
    ];

    fieldIds.forEach((fieldObject) => {
      const { field, value } = fieldObject;
      if (value) {
        this.NoteFormGroup.addControl(
          field,
          new FormControl<number>(value, { nonNullable: true })
        );
      }
    });
  }
}
